import { fromUnixTime } from "date-fns/esm";
import { useCallback } from "react";

import { convertCronExpression } from "components/FormFields/CronExpressionsField/helpers";
import KeyValue from "components/KeyValue";
import Badge from "ds/components/Badge";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import CardCollapsibleWrapper from "ds/components/Card/Collapsible/components/Wrapper";
import CardCollapsibleHeader from "ds/components/Card/Collapsible/components/Header";
import CardCollapsibleContent from "ds/components/Card/Collapsible/components/Content";
import CardContent from "ds/components/Card/components/Content";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import FeedbackActions from "ds/components/Feedback/Actions";
import Typography from "ds/components/Typography";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import { SCHEDULING_TYPE } from "shared/Stack/Scheduling/types";
import { ScheduledTask } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";

import ScheduledDateDisplay from "../components/ScheduledDateDisplay";
import { StackSchedulingContextApi } from "../Context";
import { getNextSchedule } from "../helpers";
import useStackSchedulingAccess from "../useAccess";
import { showManageScheduledTaskConfirmationModal } from "./ConfirmationModal";
import useDeleteScheduledTask from "./useDeleteScheduledTask";

type StackManageScheduledTaskListItemProps = {
  integration: ScheduledTask;
  hasPrivateWorkerPool: boolean;
};

const StackManageScheduledTaskListItem = ({
  integration,
  hasPrivateWorkerPool,
}: StackManageScheduledTaskListItemProps) => {
  const [isCollapsed, toggleState] = useToggle(false);
  const toggle = useCallback(() => toggleState(), [toggleState]);
  const { canUpdateTask, canDeleteTask } = useStackSchedulingAccess();
  const { onEdit } = useTypedContext(StackSchedulingContextApi);
  const { deleteScheduledTask, loading } = useDeleteScheduledTask();

  const onDelete = useCallback(() => {
    deleteScheduledTask(integration.id);
  }, [deleteScheduledTask, integration.id]);

  const handleConfirmDelete = useCallback(() => {
    showManageScheduledTaskConfirmationModal({
      onConfirm: onDelete,
    });
  }, [onDelete]);

  const handleEdit = useCallback(() => {
    onEdit(integration);
  }, [onEdit, integration]);

  const isRecurring = !!integration.cronSchedule;
  const isFinished = !integration.nextSchedule && !isRecurring;
  const isBlocked = !hasPrivateWorkerPool;

  return (
    <CardCollapsibleWrapper isCollapsed={isCollapsed} onToggle={toggle}>
      <CardCollapsibleHeader
        ariaLevel={3}
        ariaLabel="Task"
        suffix={
          <>
            {isFinished && <Badge variant="gray" type="strong" text="FINISHED" />}

            {(canUpdateTask.result || canDeleteTask.result) && (
              <DropdownMenuEllipsis tooltip="Scheduled task actions" buttonVariant="ghost">
                <DropdownMenuItem
                  onAction={handleEdit}
                  isDisabled={!canUpdateTask.result}
                  tooltip={canUpdateTask.message}
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  loading={loading}
                  onAction={handleConfirmDelete}
                  isDisabled={!canDeleteTask.result}
                  tooltip={canDeleteTask.message}
                  danger
                  analyticsPage={AnalyticsPageStack.StackScheduling}
                  analyticsTitle="Schedule Delete Clicked"
                  analyticsProps={{ type: SCHEDULING_TYPE.TASK }}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuEllipsis>
            )}
          </>
        }
      >
        Task
      </CardCollapsibleHeader>

      {isBlocked && (
        <CardContent hasHeader>
          <Banner
            variant="danger"
            title="Scheduled Task jobs for a given stack are not scheduling:"
            fullWidth
          >
            The stack uses an unsupported public worker pool.
            <FeedbackActions>
              <LinkButton
                variant="secondary"
                size="small"
                href={getDocsUrl("/concepts/stack/scheduling#schedule-task")}
                rel="noopener noreferrer"
                target="_blank"
              >
                Read more
              </LinkButton>
            </FeedbackActions>
          </Banner>
        </CardContent>
      )}

      <CardCollapsibleContent hasHeader>
        <KeyValue name="Command">{integration.command}</KeyValue>

        {!isBlocked && isRecurring && integration.nextSchedule && (
          <KeyValue name="Starts in">{getNextSchedule(integration.nextSchedule)}</KeyValue>
        )}

        {integration.timestampSchedule && (
          <KeyValue name="Scheduled date">
            <ScheduledDateDisplay date={fromUnixTime(integration.timestampSchedule)} />
          </KeyValue>
        )}

        {integration.cronSchedule && (
          <KeyValue name="Scheduled">
            <Box direction="column">
              {integration.cronSchedule.map((item, i, { length }) => (
                <Typography key={`${item}.${i}`} variant="p-body3" tag="span">
                  {length > 1 && " •"} {convertCronExpression(item) ?? item}
                </Typography>
              ))}
            </Box>
          </KeyValue>
        )}

        {integration.timezone && <KeyValue name="Timezone">{integration.timezone}</KeyValue>}
      </CardCollapsibleContent>
    </CardCollapsibleWrapper>
  );
};

export default StackManageScheduledTaskListItem;
